// src/pages/Imoveis.jsx  (ou onde esse componente está salvo)
import React from 'react';
import { getAlgoliaClient, getGroups } from '../services/searchClient';
import { auth } from '../services/firebase';

const INDEX_NAME = import.meta.env.ALGOLIA_INDEX || 'imoveis';

// mesmo slug do backend
const toSlug = (s = '') =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

function formatMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

/** constrói filtro de ownerId conforme checkboxes. */
function buildOwnerFilter(scope, groups) {
  const selected = [];
  if (scope.meus && groups.me) selected.push(groups.me);
  if (scope.socios && groups.socios?.length) selected.push(...groups.socios);
  if (scope.parceiros && groups.parceiros?.length)
    selected.push(...groups.parceiros);

  const total =
    (groups.me ? 1 : 0) +
    (groups.socios?.length || 0) +
    (groups.parceiros?.length || 0);

  if (selected.length === 0 || selected.length === total) return null; // não restringe
  return selected.map((id) => `ownerId:${id}`); // OR
}

export default function Imoveis() {
  // filtros
  const [q, setQ] = React.useState('');
  const [estado, setEstado] = React.useState('');
  const [cidade, setCidade] = React.useState('');
  const [bairro, setBairro] = React.useState('');
  const [tipo, setTipo] = React.useState('');
  const [precoMin, setPrecoMin] = React.useState('');
  const [precoMax, setPrecoMax] = React.useState('');

  // escopo de donos
  const [scope, setScope] = React.useState({
    meus: true,
    socios: true,
    parceiros: true,
  });
  const [groups, setGroups] = React.useState({
    me: null,
    socios: [],
    parceiros: [],
  });

  // dados
  const [hits, setHits] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [nbPages, setNbPages] = React.useState(0);
  const [facets, setFacets] = React.useState({
    estados: [],
    cidades: [],
    bairros: [],
    tipos: [],
  });

  // estado
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  // carrega grupos (me/socios/parceiros) uma vez
  React.useEffect(() => {
    (async () => {
      try {
        await getAlgoliaClient(); // garante _cache
        setGroups(getGroups());
      } catch (e) {
        console.error(e);
        setErr(String(e));
      }
    })();
  }, []);

  // reinicia pagina quando algum filtro muda
  React.useEffect(() => {
    setPage(0);
  }, [q, estado, cidade, bairro, tipo, precoMin, precoMax, scope]);

  const buildParams = React.useCallback(() => {
    const facetFilters = [];
    const ownerOr = buildOwnerFilter(scope, groups);
    if (ownerOr) facetFilters.push(ownerOr);

    // UF: slug simples
    if (estado) facetFilters.push([`estado_slug:${toSlug(estado)}`]);

    // Cidade: campo cru + slug simples + full (compat)
    if (cidade) {
      const c = String(cidade).trim();
      const cidadeOR = [`cidade:${c}`, `cidade_slug:${toSlug(c)}`];
      if (estado) cidadeOR.push(`cidade_slug_full:${toSlug(`${estado}-${c}`)}`);
      facetFilters.push(cidadeOR);
    }

    // Bairro: campo cru + slug simples + full (compat)
    if (bairro) {
      const b = String(bairro).trim();
      const bairroOR = [`bairro:${b}`, `bairro_slug:${toSlug(b)}`];
      if (estado && cidade) {
        bairroOR.push(`bairro_slug_full:${toSlug(`${estado}-${cidade}-${b}`)}`);
      }
      facetFilters.push(bairroOR);
    }

    // Tipo (mantém como está)
    if (tipo) facetFilters.push([`tipo:${tipo}`]);

    // Numéricos
    const numericFilters = [];
    if (precoMin) numericFilters.push(`preco>=${Number(precoMin)}`);
    if (precoMax) numericFilters.push(`preco<=${Number(precoMax)}`);

    return {
      query: q,
      facetFilters,
      numericFilters,
      hitsPerPage: 12,
      page,
    };
  }, [
    q,
    estado,
    cidade,
    bairro,
    tipo,
    precoMin,
    precoMax,
    page,
    scope,
    groups,
  ]);

  const buscar = React.useCallback(async () => {
    try {
      setLoading(true);
      setErr('');
      const client = await getAlgoliaClient();
      const index = client.initIndex(INDEX_NAME);

      const params = buildParams();
      const res = await index.search(params.query, params);
      setHits(res.hits);
      setNbPages(res.nbPages);

      // Facetas: buscar raw + slug (e full para compat)
      const facetsParams = {
        facets: [
          'estado_slug',
          'cidade',
          'cidade_slug',
          'bairro',
          'bairro_slug',
          'bairro_slug_full',
          'tipo',
        ],
        maxValuesPerFacet: 300,
        facetFilters: params.facetFilters?.length
          ? params.facetFilters
          : undefined,
      };
      const facetsRes = await index.search('', facetsParams);

      const estados = Object.keys(facetsRes.facets?.estado_slug || {}).map(
        (s) => s.toUpperCase()
      );

      const cidadesRaw = Object.keys(facetsRes.facets?.cidade || {});
      const cidadesFromSlug = Object.keys(
        facetsRes.facets?.cidade_slug || {}
      ).map((s) => {
        const parts = s.split('-');
        return parts.length > 1 ? parts.slice(1).join('-') : s;
      });
      const cidades = [...new Set([...cidadesRaw, ...cidadesFromSlug])].sort();

      const bairrosRaw = Object.keys(facetsRes.facets?.bairro || {});
      const bairrosFromSlugSimple = Object.keys(
        facetsRes.facets?.bairro_slug || {}
      ).map((s) => {
        const parts = s.split('-');
        return parts.length > 2 ? parts.slice(2).join('-') : s; // cobre slug full “estado-cidade-bairro” ou simples “bairro”
      });
      const bairrosFromSlugFull = Object.keys(
        facetsRes.facets?.bairro_slug_full || {}
      ).map((s) => s.split('-').slice(2).join('-'));

      const bairros = [
        ...new Set([
          ...bairrosRaw,
          ...bairrosFromSlugSimple,
          ...bairrosFromSlugFull,
        ]),
      ].sort();

      const tipos = Object.keys(facetsRes.facets?.tipo || {});

      setFacets({
        estados: estados,
        cidades,
        bairros,
        tipos: [...new Set(tipos)].sort(),
      });
    } catch (e) {
      console.error('Algolia error:', e);
      setErr(String(e));
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  React.useEffect(() => {
    buscar();
  }, [buscar]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">Explorar Imóveis</h1>
        <div className="text-xs opacity-60">
          UID: {auth.currentUser?.uid || '(não logado)'}
        </div>
      </div>

      {/* filtros topo */}
      <div className="grid gap-3 md:grid-cols-12">
        <input
          className="border p-2 rounded md:col-span-4"
          placeholder="Buscar por título, descrição..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        {/* escopo de owners */}
        <div className="flex items-center gap-4 md:col-span-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={scope.meus}
              onChange={(e) =>
                setScope((s) => ({ ...s, meus: e.target.checked }))
              }
            />
            Meus
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={scope.socios}
              onChange={(e) =>
                setScope((s) => ({ ...s, socios: e.target.checked }))
              }
            />
            Sócios
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={scope.parceiros}
              onChange={(e) =>
                setScope((s) => ({ ...s, parceiros: e.target.checked }))
              }
            />
            Parceiros
          </label>
        </div>

        <select
          className="border p-2 rounded md:col-span-2"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
        >
          <option value="">Tipo</option>
          {facets.tipos.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <div className="flex gap-2 md:col-span-2">
          <input
            className="border p-2 rounded w-full"
            placeholder="Preço mín"
            value={precoMin}
            onChange={(e) => setPrecoMin(e.target.value.replace(/\D/g, ''))}
          />
          <input
            className="border p-2 rounded w-full"
            placeholder="Preço máx"
            value={precoMax}
            onChange={(e) => setPrecoMax(e.target.value.replace(/\D/g, ''))}
          />
        </div>

        <select
          className="border p-2 rounded md:col-span-2"
          value={estado}
          onChange={(e) => {
            setEstado(e.target.value);
            setCidade('');
            setBairro('');
          }}
        >
          <option value="">Estado</option>
          {facets.estados.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <select
          className="border p-2 rounded md:col-span-2"
          value={cidade}
          onChange={(e) => {
            setCidade(e.target.value);
            setBairro('');
          }}
          disabled={!estado}
        >
          <option value="">Cidade</option>
          {facets.cidades.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>

        <select
          className="border p-2 rounded md:col-span-2"
          value={bairro}
          onChange={(e) => setBairro(e.target.value)}
          disabled={!cidade}
        >
          <option value="">Bairro</option>
          {facets.bairros.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {/* resultados */}
      {err && <div className="text-red-600">Erro: {err}</div>}
      {loading ? (
        <div>Carregando…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {hits.length === 0 && (
            <div className="col-span-full">Nenhum imóvel encontrado.</div>
          )}
          {hits.map((h) => (
            <div key={h.objectID} className="border rounded p-3">
              <div className="font-medium">{h.titulo || '(sem título)'}</div>
              <div className="text-sm opacity-70">
                {h.cidade || '-'}/{h.estado || '-'}
              </div>
              <div className="text-sm">{formatMoney(h.preco)}</div>
              {h.descricao && (
                <div className="text-xs opacity-60 mt-2 line-clamp-3">
                  {h.descricao}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* paginação */}
      <div className="flex items-center gap-3 pt-2">
        <button
          className="border px-3 py-1 rounded"
          disabled={page <= 0}
          onClick={() => setPage((p) => p - 1)}
        >
          « Anterior
        </button>
        <span>
          Página {page + 1} de {Math.max(nbPages, 1)}
        </span>
        <button
          className="border px-3 py-1 rounded"
          disabled={page >= nbPages - 1}
          onClick={() => setPage((p) => p + 1)}
        >
          Próxima »
        </button>
      </div>
    </div>
  );
}
